import { FormUnit } from "@foodpilot/foods";
import { Grid2, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ImpactComparison } from "../Common/ImpactComparison";
import { ExtendedImpactData } from "../../../../context/utils/impactData/formatComparisonTable";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";

type Props = {
    extendedImpactData: ExtendedImpactData[];
    total: number | null;
};
export const OneActionRow = (props: Props) => {
    const { extendedImpactData, total } = props;
    const { t } = useTranslation();

    const { lastSelectedVariation, selectedScorePrecision, formatScore, getColor } = useEntityScores();

    const theme = useTheme();
    const maxLineIndex = extendedImpactData.length - 1;

    const _total = formatScore(total);
    return (
        <Grid2
            container
            columns={12}
            bgcolor={theme.custom.grey[100]}
            sx={{
                borderBottomLeftRadius: "12px",
                borderBottomRightRadius: "12px",
                borderTop: `1px solid ${theme.custom.grey[600]}`,
            }}
        >
            <Grid2
                size={12}
                container
                columns={12}
                bgcolor={theme.custom.grey[200]}
                height={"64px"}
                paddingInline={theme.spacing(2)}
                alignItems={"center"}
            >
                <Grid2 size={8}>
                    <Typography variant="body-bold">{t("action_plan.action.indicators", "Indicator")}</Typography>
                </Grid2>
                <Grid2 size={4} textAlign={"end"}>
                    <Typography variant={"body-bold"}>{t("action_plan.action.impact", "Impact")}</Typography>
                </Grid2>
            </Grid2>

            <Grid2
                size={12}
                container
                columns={12}
                sx={
                    {
                        // border: `1px solid orange`,
                    }
                }
            >
                {extendedImpactData.map((impact, index) => {
                    const hideBottomBorder = index === maxLineIndex && total === null;
                    const score = impact.impactScores?.[lastSelectedVariation.scoreId];
                    return (
                        <Grid2
                            key={`${impact.propertyId}-${impact.actionId}-${impact.position}`}
                            size={12}
                            container
                            columns={12}
                            paddingInline={theme.spacing(2)}
                            height={"64px"}
                            alignItems={"center"}
                            sx={{
                                borderTop: index === 0 ? `1px solid ${theme.custom.grey[600]}` : "none",
                                borderBottom: hideBottomBorder ? "none" : `1px solid ${theme.custom.grey[600]}`,
                            }}
                        >
                            <Grid2 size={6}>
                                <Typography variant={"body"}>{impact.propertyLabel}</Typography>
                            </Grid2>
                            <Grid2 size={6} display={"inline-grid"} justifyItems={"end"}>
                                <ImpactComparison
                                    score={score ?? 0}
                                    value={{
                                        start: impact.startElement,
                                        end: impact.endElement,
                                        change: impact.elementChange,
                                        unit: impact.propertyUnit,
                                        fieldType: impact.fieldType,
                                    }}
                                />
                            </Grid2>
                        </Grid2>
                    );
                })}
            </Grid2>

            {_total !== null && (
                <Grid2
                    size={12}
                    container
                    columns={12}
                    bgcolor={theme.custom.grey[200]}
                    borderRadius="12px"
                    height={"64px"}
                    paddingInline={theme.spacing(2)}
                    alignItems={"center"}
                >
                    <Grid2 size={8}>
                        <Typography variant={"body-bold"}>{t("total", "Total")}</Typography>
                    </Grid2>
                    <Grid2 size={4} display={"inline-grid"} justifyItems={"end"}>
                        <Stack flexDirection={"row"}>
                            <ApproximateImpactTooltip
                                value={total}
                                precision={selectedScorePrecision}
                                unit={lastSelectedVariation.unit}
                            >
                                <FormUnit value={_total} unit={lastSelectedVariation.unit} color={getColor(total)} />
                            </ApproximateImpactTooltip>
                        </Stack>
                    </Grid2>
                </Grid2>
            )}
        </Grid2>
    );
};
